import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GridStateChangeV2 } from '@schenck/rotec-components';
import { Observable } from 'rxjs';
import { RotorTypeKeys } from 'src/pages/rotor-type-list/rotor-type-list.service';
import {
  RotorTypeRequest,
  PaginatedTypeData,
  TypeData,
  CalculateISOToleranceRequest,
  CalculateISOToleranceResponse,
  OffsetBearingsFromCoGRequest,
  OffsetBearingsFromCoGResponse,
  AutoCenterRequest,
  AutoCenterResponse,
  CoGFromBearingsRequest,
  CoGFromBearingsResponse,
} from 'src/utils/interfaces/rotor-type.interfaces';
import { API_BASE_URL } from './tokens';

export interface SearchParams extends GridStateChangeV2<RotorTypeKeys> {
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class TypeDataApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  private _typeDataBaseUrl: string;

  constructor() {
    const baseUrl = this.baseUrl;

    this._typeDataBaseUrl = baseUrl + '/TypeData';
  }

  public get typeDataBaseUrl(): string {
    return this._typeDataBaseUrl;
  }

  /**
   * Create rotor type data
   */
  public create$(rotorTypeData: TypeData): Observable<TypeData> {
    return this.http.post<TypeData>(this.typeDataBaseUrl, rotorTypeData);
  }

  /**
   * Duplicate rotor type data
   */
  public duplicate$(id: string, name: string): Observable<TypeData> {
    const url = `${this.typeDataBaseUrl}/${id}`;
    return this.http.post<TypeData>(url, { name });
  }

  /**
   * Get available typeData
   */
  public get$(
    page: string = '1',
    limit: string = '15',
    filter?: any
  ): Observable<PaginatedTypeData> {
    const params = {
      'PagingParams.Page': page,
      'PagingParams.Limit': limit,
    };
    const url = filter || this.typeDataBaseUrl;
    return this.http.get<PaginatedTypeData>(url, { params });
  }

  /**
   * Get selected typeData
   */
  public getById$(id: string): Observable<TypeData> {
    return this.http.get<TypeData>(`${this.typeDataBaseUrl}/${id}`);
  }

  /**
   * Update selected typeData
   */
  public update$(request: RotorTypeRequest, updatedData: TypeData): Observable<TypeData> {
    updatedData.data.rotorType.id = request.id;
    updatedData.data.rotorType.concurrencyToken = request.concurrencyToken;
    updatedData.data.rotorType.lastChange = new Date().toISOString();

    return this.http.put<TypeData>(`${this.typeDataBaseUrl}/${request.id}`, updatedData);
  }

  /**
   * Delete rotor type
   */
  public delete$({ concurrencyToken, deleteOnMachine, id }: RotorTypeRequest): Observable<void> {
    const body = {
      concurrencyToken,
      deleteOnMachine,
    };

    return this.http.request<void>('DELETE', `${this.typeDataBaseUrl}/${id}`, { body });
  }

  public calculateIsoTolerance$(
    request: CalculateISOToleranceRequest
  ): Observable<CalculateISOToleranceResponse> {
    return this.http.post<CalculateISOToleranceResponse>(
      `${this.typeDataBaseUrl}/CalculateIsoTolerance`,
      request
    );
  }

  public getBearingOffsets$(
    request: OffsetBearingsFromCoGRequest
  ): Observable<OffsetBearingsFromCoGResponse> {
    return this.http.post<OffsetBearingsFromCoGResponse>(
      `${this.typeDataBaseUrl}/bearingoffsets`,
      request
    );
  }

  public getCoG$(request: CoGFromBearingsRequest): Observable<CoGFromBearingsResponse> {
    return this.http.post<CoGFromBearingsResponse>(`${this.typeDataBaseUrl}/cog`, request);
  }

  public getAutoCenter$(request: AutoCenterRequest): Observable<AutoCenterResponse> {
    return this.http.post<AutoCenterResponse>(`${this.typeDataBaseUrl}/autocenter `, request);
  }
}
